<template>
  <section class="page" data-testid="page-overview">
    <header class="page-header">
      <div><h1>概览</h1><p>人格运行、核心资源、发送节奏和组件健康。</p></div>
      <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中..." : "刷新" }}</button>
    </header>

    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <div v-if="loading && !dashboard" class="empty">正在读取运行总览...</div>

    <div class="metrics-grid">
      <MetricCard label="整体状态" :value="healthStatus" />
      <MetricCard label="安全状态" :value="securityStatus" />
      <MetricCard label="QQ 登录" :value="napcatStatusText" />
      <MetricCard label="登录建议" :value="napcatRecoveryActionLabel" />
      <MetricCard label="OneBot" :value="onebotStatus" />
      <MetricCard label="入站健康" :value="onebotIngressLabel" />
      <MetricCard label="回复就绪" :value="onebotReplyReadyLabel" />
      <MetricCard label="活跃人设" :value="numberOf(domain.active_personas)" />
      <MetricCard label="会话上下文" :value="numberOf(domain.sessions)" />
      <MetricCard label="记忆记录" :value="numberOf(domain.memory_records)" />
      <MetricCard label="表情包" :value="numberOf(domain.stickers)" />
      <MetricCard label="插件" :value="numberOf(domain.plugins)" />
      <MetricCard label="音色绑定" :value="numberOf(domain.voice_bindings)" />
      <MetricCard label="禁区规则" :value="numberOf(domain.taboo_rules)" />
    </div>

    <div v-if="health?.security?.status === 'degraded'" class="panel warning-panel">
      <h2>安全提示</h2>
      <p>{{ health.security.message }}</p>
    </div>

    <div v-if="napcatNeedsAttention" class="panel warning-panel">
      <h2>QQ 登录提示</h2>
      <p>{{ napcatLoginHint }}</p>
      <p v-if="napcatRecoveryAction">建议动作：{{ napcatRecoveryActionLabel }}</p>
      <div class="actions overview-actions">
        <a class="button primary" href="#/qqlogin">{{ napcatRecoveryAction === "quick_login" ? "去快速登录" : "处理 QQ 登录" }}</a>
        <a class="button" href="#/observability">监控详情</a>
      </div>
    </div>

    <div v-if="dashboard && onebotIngressLevel !== 'ok'" class="panel warning-panel">
      <h2>OneBot 入站提示</h2>
      <p>{{ onebotIngressSummary }}</p>
      <p v-if="onebotReplyBlocker">阻断原因：{{ onebotReplyBlockerLabel }}</p>
      <p v-if="onebotRecoveryAction">建议动作：{{ onebotRecoveryActionLabel }}</p>
      <div class="actions overview-actions">
        <a v-if="onebotRecoveryPrefersLogin" class="button primary" href="#/qqlogin">处理 QQ 登录</a>
        <button
          class="button"
          :class="{ primary: onebotRecoveryPrefersReconnect }"
          :disabled="onebotReconnecting || !dashboard"
          @click="reconnectOneBot"
        >
          {{ onebotReconnecting ? "重连中..." : (onebotRecoveryPrefersReconnect ? "按建议重连 OneBot" : "重连 OneBot") }}
        </button>
        <a class="button" href="#/observability">监控详情</a>
        <a v-if="!onebotRecoveryPrefersLogin" class="button" href="#/qqlogin">QQ 登录</a>
      </div>
    </div>

    <div class="split">
      <div class="panel">
        <h2>人格运行</h2>
        <div class="runtime-grid">
          <div><span>情绪</span><strong>{{ personality.emotion?.style || "未启用" }}</strong></div>
          <div><span>愉悦</span><strong>{{ formatNumber(personality.emotion?.state?.valence) }}</strong></div>
          <div><span>兴奋</span><strong>{{ formatNumber(personality.emotion?.state?.arousal) }}</strong></div>
          <div><span>在场感</span><strong>{{ personality.presence?.enabled === false ? "关闭" : "开启" }}</strong></div>
          <div><span>回复概率上限</span><strong>{{ percent(personality.presence?.max_reply_probability) }}</strong></div>
          <div><span>喊名加成</span><strong>{{ percent(personality.presence?.bot_name_boost) }}</strong></div>
          <div><span>主动发言</span><strong>{{ personality.proactive?.enabled ? "开启" : "关闭" }}</strong></div>
          <div><span>主动群数</span><strong>{{ personality.proactive?.groups?.length || 0 }}</strong></div>
          <div><span>主动触发</span><strong>{{ personality.proactive?.runtime?.trigger_count || 0 }}</strong></div>
        </div>
      </div>

      <div class="panel">
        <h2>发送与模型</h2>
        <div class="runtime-grid">
          <div><span>发送调度</span><strong>{{ delivery.enabled === false ? "关闭" : "开启" }}</strong></div>
          <div><span>最近计划</span><strong>{{ delivery.summary?.recent_count || 0 }}</strong></div>
          <div><span>Stale</span><strong>{{ delivery.summary?.stale_count || 0 }}</strong></div>
          <div><span>平均等待</span><strong>{{ formatMs(delivery.summary?.avg_actual_delay_ms) }}</strong></div>
          <div><span>LLM 请求</span><strong>{{ numberOf(llmUsage.total_requests) }}</strong></div>
          <div><span>LLM Token</span><strong>{{ numberOf(llmUsage.total_tokens) }}</strong></div>
        </div>
      </div>
    </div>

    <div class="split">
      <div class="panel">
        <h2>资源规模</h2>
        <DataTable :columns="resourceColumns" :rows="resourceRows" row-key="key" />
      </div>
      <div class="panel">
        <h2>数据目录</h2>
        <DataTable :columns="storageColumns" :rows="storageRows" row-key="key">
          <template #exists="{ row }"><StatusBadge :status="row.exists ? 'ok' : 'down'">{{ row.exists ? "存在" : "缺失" }}</StatusBadge></template>
          <template #bytes="{ row }">{{ formatBytes(row.bytes) }}</template>
        </DataTable>
      </div>
    </div>

    <div class="panel">
      <div class="panel-header">
        <div>
          <h2>命令目录</h2>
          <p>{{ commandSummaryText }}</p>
        </div>
        <div class="toolbar compact-toolbar">
          <input v-model="commandQuery" placeholder="搜索命令或用法" />
          <button class="button" :class="{ primary: commandScope === 'all' }" @click="commandScope = 'all'">全部</button>
          <button class="button" :class="{ primary: commandScope === 'public' }" @click="commandScope = 'public'">公开</button>
          <button class="button" :class="{ primary: commandScope === 'owner' }" @click="commandScope = 'owner'">Owner</button>
        </div>
      </div>
      <div v-if="suggestedCommands.length" class="command-suggestions">
        <button v-for="item in suggestedCommands" :key="item.command" class="button command-pill" @click="copyCommand(item.command)">
          <strong>{{ item.command }}</strong>
          <span>{{ item.label }}</span>
        </button>
      </div>
      <DataTable :columns="commandColumns" :rows="filteredCommands" row-key="command">
        <template #command="{ row }"><code>{{ row.command }}</code></template>
        <template #scope="{ row }"><StatusBadge :status="row.scope === 'all' ? 'ok' : 'warning'">{{ row.scope === "all" ? "公开" : "Owner" }}</StatusBadge></template>
        <template #usage="{ row }">
          <div class="usage-list">
            <span v-for="line in visibleUsage(row)" :key="line">{{ line }}</span>
          </div>
        </template>
        <template #action="{ row }"><button class="button small" @click="copyCommand(row.command)">复制</button></template>
      </DataTable>
    </div>

    <div class="panel">
      <h2>组件健康</h2>
      <DataTable :columns="columns" :rows="componentRows" row-key="name">
        <template #status="{ row }"><StatusBadge :status="row.status" /></template>
        <template #elapsed_ms="{ row }">{{ formatMs(row.elapsed_ms) }}</template>
      </DataTable>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api } from "../api/client";
import type { CommandCatalogItem, CommandCatalogPayload, DashboardPayload, NapCatStatusPayload, OneBotRuntimePayload } from "../api/types";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useOneBotReconnect } from "../composables/useOneBotReconnect";
import { napcatLoginRecoveryActionLabel as formatNapcatLoginRecoveryActionLabel } from "../diagnostics/napcat";
import {
  onebotDiagnosisLabel,
  onebotIngressLevelLabel,
  onebotIngressReason,
  onebotIngressSummary as formatOnebotIngressSummary,
  onebotRecoveryActionLabel as formatOnebotRecoveryActionLabel,
  onebotReplyReadyLabel as formatOnebotReplyReadyLabel,
  onebotStateLabel
} from "../diagnostics/onebot";
import DataTable from "../ui/DataTable.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";

const dashboard = ref<DashboardPayload | null>(null);
const commandCatalog = ref<CommandCatalogPayload | null>(null);
const napcatStatus = ref<Partial<NapCatStatusPayload>>({});
const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const { copyText } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择命令");
const { reconnecting: onebotReconnecting, reconnectOneBot } = useOneBotReconnect({
  actionResult,
  error,
  onSuccess() {
    window.setTimeout(load, 1800);
  }
});
const commandQuery = ref("");
const commandScope = ref<"all" | "public" | "owner">("all");
const health = computed(() => dashboard.value?.health || null);
const domain = computed(() => dashboard.value?.domain || {});
const stats = computed(() => dashboard.value?.stats || {});
const storage = computed(() => dashboard.value?.storage || {});
const delivery = computed(() => dashboard.value?.delivery_timing || {});
const personality = computed(() => dashboard.value?.personality || {});
const llmUsage = computed(() => dashboard.value?.llm_usage || {});
const healthStatus = computed(() => health.value?.status || "--");
const securityStatus = computed(() => health.value?.security?.status || dashboard.value?.security?.status || "--");
const napcatRecoveryAction = computed(() => String(napcatStatus.value.login_recovery_action || ""));
const napcatRecoveryActionLabel = computed(() => formatNapcatLoginRecoveryActionLabel(napcatRecoveryAction.value));
const napcatStatusText = computed(() => {
  if (!Object.keys(napcatStatus.value).length) return "--";
  if (napcatStatus.value.is_online) return "在线";
  if (napcatStatus.value.is_login) return "已登录/离线";
  if (napcatStatus.value.has_account) return "未登录/有缓存";
  return "未登录";
});
const napcatNeedsAttention = computed(() => Boolean(Object.keys(napcatStatus.value).length && !napcatStatus.value.is_online));
const napcatLoginHint = computed(() => {
  if (napcatStatus.value.login_error) return `NapCat: ${napcatStatus.value.login_error}`;
  if (napcatStatus.value.login_recovery_hint) return napcatStatus.value.login_recovery_hint;
  return "QQ 当前不在线，打开 QQ 登录页处理二维码或快速登录。";
});
const onebotRuntime = computed(() => {
  const statsRuntime = (stats.value as { onebot?: OneBotRuntimePayload }).onebot;
  const detailedRuntime = health.value?.components?.onebot?.runtime;
  return statsRuntime || detailedRuntime || health.value?.onebot || {};
});
const onebotStatus = computed(() => {
  if ((stats.value as { onebot_connected?: boolean }).onebot_connected || health.value?.onebot_connected || health.value?.onebot?.connected || onebotRuntime.value.connected) {
    return "已连接";
  }
  return onebotStateLabel(onebotRuntime.value.state);
});
const onebotIngress = computed(() => onebotRuntime.value?.ingress_health || {});
const onebotIngressVerdict = computed(() => onebotRuntime.value?.ingress_verdict || {});
const onebotIngressLevel = computed(() => String(onebotIngress.value?.level || (onebotRuntime.value?.connected ? "waiting" : "error")));
const onebotIngressLabel = computed(() => onebotIngressLevelLabel(onebotIngressLevel.value, onebotIngressReason(onebotRuntime.value, onebotIngress.value)));
const onebotIngressSummary = computed(() => formatOnebotIngressSummary(onebotRuntime.value, Boolean(onebotRuntime.value?.connected), onebotIngress.value, onebotIngressVerdict.value));
const onebotReplyReadyLabel = computed(() => formatOnebotReplyReadyLabel(onebotRuntime.value));
const onebotReplyBlocker = computed(() => String(onebotRuntime.value?.reply_blocker || ""));
const onebotReplyBlockerLabel = computed(() => onebotDiagnosisLabel(onebotReplyBlocker.value));
const onebotRecoveryAction = computed(() => String(onebotIngressVerdict.value?.recovery_action || ""));
const onebotRecoveryActionLabel = computed(() => formatOnebotRecoveryActionLabel(onebotRecoveryAction.value));
const onebotRecoveryPrefersLogin = computed(() => ["login_qq", "login_qq_or_check_onebot"].includes(onebotRecoveryAction.value) || onebotIngressVerdict.value?.code === "qq_not_logged_in");
const onebotRecoveryPrefersReconnect = computed(() => [
  "reconnect_onebot",
  "reconnect_onebot_or_restart_napcat",
  "restart_bot_or_onebot"
].includes(onebotRecoveryAction.value));
const columns = [
  { key: "name", label: "组件" },
  { key: "status", label: "状态" },
  { key: "detail", label: "详情" },
  { key: "elapsed_ms", label: "耗时 ms" }
];
const resourceColumns = [
  { key: "label", label: "资源" },
  { key: "value", label: "数量" },
  { key: "why", label: "用途" }
];
const storageColumns = [
  { key: "label", label: "目录" },
  { key: "exists", label: "状态" },
  { key: "files", label: "文件" },
  { key: "bytes", label: "体积" },
  { key: "path", label: "路径" }
];
const commandColumns = [
  { key: "command", label: "命令" },
  { key: "scope", label: "权限" },
  { key: "description", label: "说明" },
  { key: "usage", label: "用法" },
  { key: "action", label: "操作" }
];
const componentRows = computed(() => Object.entries(health.value?.components || {}).map(([name, value]) => ({ name, ...(value || {}) })));
const commandRows = computed(() => commandCatalog.value?.commands || []);
const commandSummaryText = computed(() => {
  const summary = commandCatalog.value?.summary;
  if (!summary) return "读取命令目录中";
  return `${summary.total} 个命令，公开 ${summary.public} 个，Owner ${summary.owner} 个，${summary.usage_lines} 条用法`;
});
const filteredCommands = computed(() => {
  const text = commandQuery.value.trim().toLowerCase();
  return commandRows.value.filter((item) => {
    if (commandScope.value === "public" && item.scope !== "all") return false;
    if (commandScope.value === "owner" && item.scope !== "owner") return false;
    if (!text) return true;
    return [
      item.command,
      item.description,
      item.scope,
      ...(item.public_usage || []),
      ...(item.owner_usage || [])
    ].join(" ").toLowerCase().includes(text);
  });
});
const suggestedCommands = computed(() => {
  const items: { command: string; label: string }[] = [];
  if (napcatNeedsAttention.value) {
    items.push({ command: "/ping", label: "登录后验活" });
  } else if (onebotIngressLevel.value !== "ok") {
    items.push({ command: "/ping", label: "入口排障" });
  } else {
    items.push({ command: "/who", label: "群友视角" });
  }
  items.push({ command: "/persona", label: "人设列表" });
  items.push({ command: "/who", label: "当前人格" });
  if (onebotIngressLevel.value !== "ok" || !onebotRuntime.value?.reply_ready) {
    items.push({ command: "/why", label: "最近不回复" });
  }
  return uniqueSuggestedCommands(items);
});
const resourceRows = computed(() => [
  { key: "personas", label: "人设", value: numberOf(domain.value.personas), why: "可切换人格资产" },
  { key: "active_personas", label: "活跃人设", value: numberOf(domain.value.active_personas), why: "群/会话当前绑定" },
  { key: "sessions", label: "会话上下文", value: numberOf(domain.value.sessions), why: "机器人记得的群友现场" },
  { key: "memory_records", label: "记忆记录", value: numberOf(domain.value.memory_records), why: "RAG 长期记忆" },
  { key: "stickers", label: "表情包", value: numberOf(domain.value.stickers), why: "像群友一样接梗" },
  { key: "voices", label: "音色", value: numberOf(domain.value.voices), why: "语音人格素材" },
  { key: "voice_bindings", label: "音色绑定", value: numberOf(domain.value.voice_bindings), why: "人设专属声音" },
  { key: "taboo_rules", label: "禁区规则", value: numberOf(domain.value.taboo_rules), why: "风险话题降级" },
  { key: "attitudes", label: "态度对象", value: numberOf(domain.value.attitudes), why: "关系差异化回应" }
]);
const storageRows = computed(() => {
  const labels: Record<string, string> = {
    personas: "人设",
    chat_logs: "聊天日志",
    memory: "记忆",
    stickers: "表情包",
    voices: "音色",
    uploads: "上传",
    metrics: "指标"
  };
  return Object.entries(storage.value).map(([key, value]) => ({
    key,
    label: labels[key] || key,
    ...(value || {})
  }));
});
async function load() {
  loading.value = true;
  error.value = "";
  try {
    const [dashboardPayload, commandPayload, napcatPayload] = await Promise.all([
      api<DashboardPayload>("/api/monitoring/dashboard"),
      api<CommandCatalogPayload>("/api/commands"),
      api<NapCatStatusPayload>("/api/napcat/status").catch(() => ({}))
    ]);
    dashboard.value = dashboardPayload;
    commandCatalog.value = commandPayload;
    napcatStatus.value = napcatPayload;
  } catch (err: any) {
    error.value = err?.message || "运行总览加载失败";
  } finally {
    loading.value = false;
  }
}
function numberOf(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}
function formatNumber(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "-";
}
function percent(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n * 1000) / 10}%` : "-";
}
function formatMs(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${Math.round(n)}ms`;
}
function formatBytes(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}
function visibleUsage(row: CommandCatalogItem) {
  if (commandScope.value === "owner") return row.owner_usage || [];
  if (commandScope.value === "public") return row.public_usage || [];
  return [...(row.public_usage || []), ...(row.owner_usage || [])];
}
function uniqueSuggestedCommands(items: { command: string; label: string }[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.command)) return false;
    seen.add(item.command);
    return true;
  });
}
async function copyCommand(command: string) {
  await copyText(command, `已复制 ${command}`);
}
onMounted(load);
</script>

<style scoped>
.warning-panel { border-color: #78612d; background: #241f14; }
.runtime-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
}
.runtime-grid div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
  min-width: 0;
}
.runtime-grid span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}
.runtime-grid strong {
  display: block;
  margin-top: 6px;
  font-size: 18px;
  overflow-wrap: anywhere;
}
.usage-list {
  display: grid;
  gap: 4px;
}
.usage-list span {
  color: var(--text);
  overflow-wrap: anywhere;
}
.command-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;
}
.command-pill {
  min-height: 42px;
  align-items: flex-start;
  flex-direction: column;
  gap: 2px;
}
.command-pill strong {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
}
.command-pill span {
  color: var(--muted);
  font-size: 12px;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
}
.panel-header h2 { margin-bottom: 4px; }
.panel-header p { color: var(--muted); margin: 0; }
.compact-toolbar {
  justify-content: flex-end;
  flex-wrap: wrap;
}
.compact-toolbar input {
  min-width: 180px;
}
@media (max-width: 860px) {
  .panel-header {
    display: grid;
  }
  .compact-toolbar {
    justify-content: flex-start;
  }
}
</style>
